import { cn } from "../../../lib/utils";
import { useTranslation } from 'react-i18next';
import { currencyFormatter } from "../../../services/helperService";
import { PriceAlert } from "../../../services/priceAlerts.ts/priceAlertsService";
import {
  baseTextSize,
  extraLargeTextSize,
  quaternaryText,
  primaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  whiteBG,
  largeTextSize,
  mediumText,
  borderGrey
} from "../../../styling/stylingHelper";
import { renderByType } from "../../ui/TableValueComponent";
import { Platform } from "../../../types/Platforms";
import { getTableFormattedDate } from "../../../utils/dateUtils";
import { useState, useRef, useLayoutEffect, useMemo } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { isClickablePlatform, getSupplierUrl } from "../../../utils/platformUtils";
import { SupplierLogo } from "../../ui/SupplierLogo";
import { useAppSettingsStore } from "../../../stores/appSettingsStore";

/** Deterministic hash of a string to an 8-digit number string. */
function hashToEightDigits(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
  }
  const positive = Math.abs(hash);
  return String(positive % 100_000_000).padStart(8, '0');
}

export interface PriceAlertComponentProps {
  alert: PriceAlert;
  isDemoAccount?: boolean;
  restaurantName?: string;
}

function LabelValue({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className="mb-2 flex flex-row items-center gap-2">
      <div className={cn(smallTextSize, tertiaryText, mediumText)}>{label}:</div>
      <div className={cn(smallTextSize, semiBoldText, primaryText, className)}>{value}</div>
    </div>
  );
}

// Improved Collapsible section with fast transition (height animation)
function Collapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("0px"); // default collapsed
  const [render, setRender] = useState(open);

  // When open changes, update render state for mounting/unmounting nodes
  useLayoutEffect(() => {
    if (open) setRender(true);
  }, [open]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (open) {
      // Set initial height to 0, force paint, then expand to scrollHeight
      setHeight("0px");
      // Force paint so browser recognizes 0px start
      requestAnimationFrame(() => {
        if (!el) return;
        const scrollHeight = el.scrollHeight;
        setHeight(scrollHeight + "px");
        // After transition, set height to auto for dynamic height children
        const tid = setTimeout(() => {
          setHeight("auto");
        }, 120);
        // Clean-up
        return () => clearTimeout(tid);
      });
    } else {
      // Set to fixed height before collapsing smoothly to 0
      if (height === "auto") setHeight(el.scrollHeight + "px");
      // This animation should work perfectly for closing
      requestAnimationFrame(() => {
        setHeight("0px");
      });
      // Unmount after transition finishes
      const timeout = setTimeout(() => setRender(false), 120);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        height: render ? height : "0px",
        overflow: "hidden",
        transition: "height 120ms cubic-bezier(0.4, 0, 0.2, 1)",
        display: render ? "block" : "none",
      }}
      aria-hidden={!open}
    >
      {render ? children : null}
    </div>
  );
}

/**
 * Responsive layout using Tailwind's arbitrary breakpoint min-[1150px]
 */
export const PriceAlertComponent = ({
  alert,
  isDemoAccount = false,
  restaurantName,
}: PriceAlertComponentProps) => {
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const [isOpen, setIsOpen] = useState(false);

  const percentChange = ((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100;
  const isPositive = percentChange >= 0;

  // For demo accounts, replace ART-xxx article IDs with a deterministic 8-digit number
  const displayArticleId = useMemo(() => {
    if (isDemoAccount && alert.articleId && alert.articleId.startsWith('ART-')) {
      return hashToEightDigits(alert.articleId);
    }
    return alert.articleId;
  }, [isDemoAccount, alert.articleId]);

  return (
    <div className={cn("w-full rounded-xl px-3", whiteBG)}>
      {/* Top Section (always shown) */}
      <button
        type="button"
        className={cn("w-full py-4 cursor-pointer flex items-center focus:outline-none select-none")}
        onClick={() => setIsOpen((v) => !v)}
      >
        {/* Expand/collapse icon */}
        <div
          className={cn(
            "flex items-center justify-center mr-4 shrink-0",
            "w-8 h-8 rounded-full",
            "bg-backgroundDarker dark:bg-backgroundDarker-dark ml-2"
          )}
        >
          {isOpen
            ? <ChevronDown size={20} className="text-black dark:text-textPrimary-dark font-bold" strokeWidth={2} />
            : <ChevronRight size={20} className="text-black dark:text-textPrimary-dark font-bold" strokeWidth={2} />
          }
        </div>

        {/* Main flex: left side (40% on desktop with category/id, flexible on mobile) */}
        <div className="flex w-full min-w-0">
          {/* Left side: 40% on >=1150px, flexible on <1150px */}
          <div
            className={cn(
              "flex flex-col justify-center overflow-hidden min-w-0",
              // min-[1150px]: flex-basis 40%, max-width 40%; else 1 1 0%
              "min-[1150px]:basis-[40%] min-[1150px]:max-w-[40%]",
              "basis-[100%] max-w-[100%] flex-1"
            )}
          >
            <span className={cn(baseTextSize, primaryText, semiBoldText, "text-left truncate")}>{alert.productName}</span>
            <div className="flex items-center mt-1 min-w-0">
              <SupplierLogo
                platform={alert.platform as Platform}
                className="shrink-0"
                alt={alert.platform || "Platform"}
              />
              {restaurantName && (
                <>
                  <span className={cn(extraLargeTextSize, quaternaryText, "mx-1 shrink-0")}>•</span>
                  <span className={cn(smallTextSize, quaternaryText, "truncate min-w-0")}>{restaurantName}</span>
                </>
              )}
              <span className={cn(extraLargeTextSize, quaternaryText, "mx-1 shrink-0")}>•</span>
              <span className={cn(smallTextSize, quaternaryText, "truncate min-w-0")}>{getTableFormattedDate(new Date(alert.toDate))}</span>
            </div>
          </div>
          {/* Right side: 60% on >=1150px (3 sections), fixed width on <1150px (1 section) */}
          <div
            className={cn(
              "flex flex-row items-center shrink-0",
              "min-[1150px]:basis-[60%] min-[1150px]:max-w-[60%]",
              // fallback on mobile: do not restrict width
            )}
          >
            {/* Show these 2 cols only on >= 1150px - hide on mobile/tablet */}
            <div className="hidden min-[1150px]:flex flex-1 items-center justify-center min-w-0 relative">
              {alert.category ? renderByType(alert.category, "category", undefined, undefined, undefined, undefined, undefined, darkMode) : "-"}
              {/* Divider, right side */}
              <div
                className="absolute right-0 top-1/2 -translate-y-1/2 border-r border-borderPrimary dark:border-borderPrimary-dark pointer-events-none"
                style={{ width: 0, height: "65%" }}
                aria-hidden
              ></div>
            </div>
            <div className="hidden min-[1150px]:flex flex-1 items-center justify-center min-w-0 relative">
              {alert.platform && isClickablePlatform(alert.platform) ? (
                <a
                  href={getSupplierUrl(alert.platform, alert.articleId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {displayArticleId}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span className="truncate">{displayArticleId}</span>
              )}
              {/* Divider, right side */}
              <div
                className="absolute right-0 top-1/2 -translate-y-1/2 border-r border-borderPrimary dark:border-borderPrimary-dark pointer-events-none"
                style={{ width: 0, height: "65%" }}
                aria-hidden
              ></div>
            </div>
            {/* Percent Change (always shown) */}
            <div className={cn(
              "flex items-center",
              "flex-1 justify-end pr-2 min-[1150px]:flex-1 min-[1150px]:justify-end min-[1150px]:pr-2",
              "justify-end pl-2"
            )}>
              <div
                className={cn(
                  "flex items-center justify-center px-3 py-1 rounded-lg shrink-0",
                  isPositive ? "bg-[#FEF3F2] dark:bg-red-900/20" : "bg-[#EDFCF5] dark:bg-[#0E8965]/20"
                )}
              >
                <span
                  className={cn(
                    smallTextSize,
                    semiBoldText,
                    "whitespace-nowrap",
                    isPositive
                      ? "text-[#D92D20] dark:text-textRed-dark"
                      : "text-[#069A6E] dark:text-darkGreenColor-dark"
                  )}
                >
                  {(isPositive ? "+" : "") +
                    percentChange.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    }) +
                    "%"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>

      {/* Expanded section with transition */}
      <Collapse open={isOpen}>
        <div className={cn(
          "flex flex-col gap-3 w-full py-4 px-2 text-left",
          isOpen ? "border-t border-borderGrey dark:border-borderGrey-dark" : ""
        )}>
          {/* Category/ID shown side by side ONLY when hidden from main section (below 1150px) */}
          <div className="block min-[1150px]:hidden w-full pb-2 flex flex-row justify-center gap-6 items-stretch max-w-[600px] mx-auto">
            {/* Category */}
            <div className="flex flex-col gap-1 flex-1 min-w-0 items-center">
              <div className={cn(smallTextSize, tertiaryText, mediumText, "text-center")}>{t('priceAlerts.categoryLabel')}</div>
              <div className="text-center">
                {alert.category ? renderByType(alert.category, "category", undefined, undefined, undefined, undefined, undefined, darkMode) : "-"}
              </div>
            </div>
            {/* ArticleId */}
            <div className="flex flex-col gap-1 flex-1 min-w-0 items-center">
              <div className={cn(smallTextSize, tertiaryText, mediumText, "text-center")}>{t('priceAlerts.productIdLabel')}</div>
              <div className="text-center">
                {alert.platform && isClickablePlatform(alert.platform) ? (
                  <a
                    href={getSupplierUrl(alert.platform, alert.articleId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline truncate"
                  >
                    {displayArticleId}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="truncate">{displayArticleId}</span>
                )}
              </div>
            </div>
          </div>
          {/* FROM box (left) and TO box (right) - side by side by default, stack on <550px */}
          <div className="flex flex-row gap-4 w-full max-[549px]:flex-col">
            {/* FROM box */}
            <div
              className={cn(
                "flex-1 p-3 rounded-xl border",
                borderGrey,
                whiteBG
              )}
            >
              <div className={cn(largeTextSize, primaryText, semiBoldText, "text-left")}>{t('priceAlerts.from')}</div>
              <LabelValue label={t('priceAlerts.priceLabel')} value={currencyFormatter.format(alert.fromPrice)} />
              <LabelValue
                label={t('priceAlerts.orderLabel')}
                value={alert.fromOrderHistoryId ? (
                  <a
                    href={`/orderhistory/${alert.fromOrderHistoryId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('priceAlerts.view')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : "-"}
              />
              <LabelValue label={t('priceAlerts.dateLabel')} value={getTableFormattedDate(new Date(alert.fromDate))} />
            </div>
            {/* TO box */}
            <div
              className={cn(
                "flex-1 p-3 rounded-xl border",
                borderGrey,
                whiteBG
              )}
            >
              <div className={cn(largeTextSize, primaryText, semiBoldText, "text-left")}>{t('priceAlerts.to')}</div>
              <LabelValue
                label={t('priceAlerts.priceLabel')}
                value={currencyFormatter.format(alert.toPrice)}
                className={isPositive
                  ? "text-[#D92D20] dark:text-textRed-dark"
                  : "text-[#069A6E] dark:text-darkGreenColor-dark"
                }
              />
              <LabelValue
                label={t('priceAlerts.orderLabel')}
                value={alert.toOrderHistoryId ? (
                  <a
                    href={`/orderhistory/${alert.toOrderHistoryId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('priceAlerts.view')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : "-"}
              />
              <LabelValue label={t('priceAlerts.dateLabel')} value={getTableFormattedDate(new Date(alert.toDate))} />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};